import { useId } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { DialogBackdrop } from '../../../components/ui/dialog-backdrop';
import { Button } from '../../../components/ui/button';
import styles from './ConfirmDialog.module.css';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  variant?: 'destructive' | 'default';
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  isPending,
  title,
  description,
  confirmLabel,
  variant = 'destructive',
}: ConfirmDialogProps) {
  const titleId = useId();

  if (!open) return null;

  return createPortal(
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialogShell} open aria-modal="true" aria-labelledby={titleId}>
        <div className={styles.dialogPanel}>
          <div className={styles.body}>
            <div className={styles.headerRow}>
              {variant === 'destructive' && (
                <div className={styles.iconWrapper}>
                  <AlertTriangle className={styles.icon} />
                </div>
              )}
              <div>
                <h3 id={titleId} className={styles.title}>
                  {title}
                </h3>
                <p className={styles.description}>{description}</p>
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button variant={variant} size="sm" onClick={onConfirm} disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className={styles.loaderIcon} />
                  Processing...
                </>
              ) : (
                confirmLabel
              )}
            </Button>
          </div>
        </div>
      </dialog>
    </>,
    document.body,
  );
}
